import { acceptHMRUpdate, defineStore } from 'pinia'

// import waMeals from '@/data/wa/meals'

export const useFatStore = defineStore('fat', {
	persist: {
		storage: piniaPluginPersistedstate.cookies({
			maxAge: 60 * 60 * 24 * 365,
		}),
	},

	state: () => ({
		status: 'pending',
		total: 0,
		date: new Date().toISOString().split('T')[0],
		amount: 2,
		cityId: null,
		excludeEmpty: true,
		startTime: null,
		endTime: null,
		preferredTime: null,
		latitude: null,
		longitude: null,
		distance: 3000,
		mealTypeIds: [],
		tagIds: [],
		restaurantIds: [],
		selectedRestaurantId: null,
		selectedTimeSlotKey: null,
		s: '',
	}),

	getters: {
		pending() {
			return this.status === 'pending'
		},
		timeRange() {
			return this.startTime && this.endTime
				? `${this.startTime}-${this.endTime}`
				: null
		},
		city() {
			const config = useNuxtApp().$configData
			return config?.wa?.cities.find(city => city.id === this.cityId)
		},
		cityIds() {
			const config = useNuxtApp().$configData
			const city = config?.wa?.cities.find(city => city.id === this.cityId)

			return city?.relatedCityIds
				? [this.cityId, ...(city?.relatedCityIds || [])]
				: [this.cityId]
		},
		cityParts() {
			const config = useNuxtApp().$configData
			const tags = config?.wa?.tags

			let tagGroupId = null

			if (this.city?.slug === 'stockholm') {
				tagGroupId = 8
			}

			if (this.city?.slug === 'goteborg') {
				tagGroupId = 9
			}

			if (this.city?.slug === 'malmo') {
				tagGroupId = 10
			}

			return tags.filter(tag => tag.groupId === tagGroupId)
		},
		meal() {
			const config = useNuxtApp().$configData

			return config?.wa?.meals.find(
				meal =>
					meal.startTime === this.startTime && meal.endTime === this.endTime,
			)
		},
		mealSlug() {
			return this.meal?.slug || undefined
		},
		mealType() {
			return this.meal?.type || undefined
		},
		defaultMealTypes() {
			return [0, 1, 2, 3, 4, 5]
		},
		location() {
			return this.latitude && this.longitude && this.distance
				? `${this.latitude},${this.longitude},${this.distance}`
				: undefined
		},
		hasLocation() {
			return !!this.location
		},
		hasUserLocation() {
			const user = useUserStore()
			return (
				this.hasLocation
				&& this.longitude === user.longitude
				&& this.latitude === user.latitude
			)
		},
		mealTypes() {
			// Use meal type 4 if tag "brunch" is active
			if (this.tagIds.includes(176)) {
				return 4
			}

			// Use meal type 11 if faux tag for offers is active, "1100, 1101, 1102, 1103"
			if ([1100, 1101, 1102, 1103].some(tag => this.tagIds.includes(tag))) {
				return 11
			}

			// Use selected meal type ID's
			if (this.mealTypeIds.length) {
				return this.mealTypeIds.join(',')
			}

			return this.defaultMealTypes.join(',')
		},
		altTypes() {
			const dayjs = useDayjs()

			// Only return alt types for offers (meal type 11)
			if (this.mealTypes !== 11) {
				return undefined
			}

			// Return alt type for 11,01 (25% på maten)
			if (
				dayjs(this.date).isBefore(dayjs().add(7, 'days'))
				&& this.tagIds.includes(1101)
			) {
				return 1
			}

			// Return alt type for 11,02 (tidig middag)
			if (dayjs(this.date).isToday() && this.tagIds.includes(1102)) {
				return 2
			}

			// Return alt type for 11,03 (sista minuten)
			if (dayjs(this.date).isToday() && this.tagIds.includes(1103)) {
				return 3
			}

			return undefined
		},
		tagIdsQuery() {
			const app = useAppStore()

			const amenityTags = app.tags
				?.filter(tag => 7 === tag.groupId)
				?.map(tag => tag.id)

			const cuisineTags = app.tags
				?.filter(tag => 2 === tag.groupId)
				?.map(tag => tag.id)

			const dishTags = app.tags
				?.filter(tag => tag.id !== 176) // Remove "brunch" from ID's to query (use mealtype 4)
				?.filter(tag => 1 === tag.groupId)
				?.map(tag => tag.id)

			const regionTags = app.tags
				?.filter(tag => [8, 9, 10].includes(tag.groupId))
				?.map(tag => tag.id)

			if (this.tagIds?.length) {
				return [
					this.tagIds.filter(tagId => amenityTags.includes(tagId)).join(';'),
					this.tagIds.filter(tagId => cuisineTags.includes(tagId)).join(','),
					this.tagIds.filter(tagId => dishTags.includes(tagId)).join(','),
					this.tagIds.filter(tagId => regionTags.includes(tagId)).join(','),
				]
					.filter(tag => !!tag)
					.join(';')
			}

			return undefined
		},
		minDate() {
			const dayjs = useDayjs()
			return dayjs().format('YYYY-MM-DD')
		},
		maxDate() {
			const dayjs = useDayjs()
			return dayjs().add(90, 'day').format('YYYY-MM-DD')
		},
		selectedRestaurantFetchKey() {
			return this.selectedRestaurantId
				? `offers-restaurant-${this.selectedRestaurantId}`
				: undefined
		},
		selectedMealAndTimeSlot() {
			const { data } = useNuxtData(`offers-${this.cityId}`)

			if (!data.value) return null

			const [mealId, time] = (this.selectedTimeSlotKey || '').split(
				'-',
			)

			const restaurant = data?.value?.data?.restaurants?.find(r => r.id === this.selectedRestaurantId)

			if (!restaurant) return null

			const meal = (restaurant.meals || []).find(meal => meal.id === Number(mealId))

			const timeslot = meal?.timeslots?.find(t => t.time === time)

			return {
				meal,
				timeslot,
			}
		},
	},

	actions: {
		resetTags() {
			useUpdateQuery({ tagIds: undefined })
			this.tagIds = []
		},
		initFromConfig() {
			const config = useNuxtApp().$configData
			const meals = config?.wa?.meals ?? []
			const dinner = meals.find(meal => meal.slug === 'dinner')

			if (dinner && !this.startTime && !this.endTime) {
				this.startTime = dinner.startTime
				this.endTime = dinner.endTime
				this.preferredTime = dinner.preferredTime
			}
		},
		setTimeRange(startTime, endTime) {
			this.startTime = startTime
			this.endTime = endTime

			// Auto update PreferredTime based on meal
			if (this.meal?.preferredTime) {
				this.preferredTime = this.meal?.preferredTime
			}

			// Auto update PreferredTime based on start and end time
			//
			// TODO
			//
		},
		setPreferredTime(preferredTime) {
			this.preferredTime = preferredTime
		},
		setPreferredTimeWithOffset(preferredTime, offsetInMinutes = 0) {
			const dayjs = useDayjs()
			this.preferredTime = preferredTime

			if (offsetInMinutes) {
				this.startTime = dayjs(preferredTime, 'HH:mm')
					.add(-offsetInMinutes, 'minutes')
					.format('HH:mm')
				this.endTime = dayjs(preferredTime, 'HH:mm')
					.add(offsetInMinutes, 'minutes')
					.format('HH:mm')
			}
		},
		setMealBySlug(mealSlug) {
			const config = useNuxtApp().$configData
			const meal = config?.wa?.meals?.find(meal => meal.slug === mealSlug)
			this.setTimeRange(meal?.startTime, meal?.endTime)
			this.setPreferredTime(meal?.preferredTime)
		},
		setLocation(latitude = null, longitude = null, distance = 3000) {
			((this.latitude = latitude),
			(this.longitude = longitude),
			(this.distance = distance ? parseInt(distance) : this.distance))
		},
		setSelectedRestaurant(id, mealTimeslot) {
			if (this.selectedRestaurantId !== id) {
				this.selectedRestaurantId = id
			}
			this.selectedTimeSlotKey = mealTimeslot || null
		},
		clearSelectedRestaurant() {
			this.selectedRestaurantId = null
			this.selectedTimeSlotKey = null
		},
		setMealTimeSlot(tsUniqueKey) {
			this.selectedTimeSlotKey = tsUniqueKey
		},
		clearMealTimeSlot() {
			this.selectedTimeSlotKey = null
		},
	},
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useFatStore, import.meta.hot))
}
